import { useCallback, useMemo } from 'react'

import { Policy } from 'src/constants'
import { useGetProfile } from 'src/hooks/useGetProfile'
import { useMasterData } from 'src/hooks/useMasterData'
import { UserEntity } from 'src/types'

type CheckRoleInput = { targetUser: UserEntity }

export const useAuthority = () => {
  const { profile, loading: profileLoading, error: profileError } = useGetProfile()

  const { masterData, loading: masterLoading, error: masterError } = useMasterData()

  const isLoading = profileLoading || masterLoading
  const isError = profileError || masterError

  const hasPolicy = useCallback(
    (policy: string) => (profile ? profile.policies.includes(policy) : false),
    [profile]
  )

  /* ------------------------- プロジェクト関連ポリシー ------------------------ */
  const canCreateProject = useMemo(() => hasPolicy(Policy.createProject), [hasPolicy])
  const canUpdateProject = useMemo(() => hasPolicy(Policy.updateProject), [hasPolicy])
  const canDeleteProject = useMemo(() => hasPolicy(Policy.deleteProject), [hasPolicy])
  const canAssignProject = useMemo(() => hasPolicy(Policy.assignProject), [hasPolicy])
  const canReadReport = useMemo(() => hasPolicy(Policy.readProjectReport), [hasPolicy])

  /* ------------------------- 共通コンテンツ関連ポリシー ------------------------ */
  const canReadCommonContent = useMemo(() => hasPolicy(Policy.readCommonContent), [hasPolicy])
  const canWriteCommonContent = useMemo(() => hasPolicy(Policy.writeCommonContent), [hasPolicy])
  const canDeleteCommonContent = useMemo(() => hasPolicy(Policy.deleteCommonContent), [hasPolicy])

  /* ------------------------- 顧客関連系ポリシー -------------------------- */
  const canOptOutCustomer = useMemo(() => hasPolicy(Policy.optOutCustomer), [hasPolicy])
  const canDeleteCustomer = useMemo(() => hasPolicy(Policy.deleteCustomer), [hasPolicy])
  const canManageCustomerStage = useMemo(() => hasPolicy(Policy.manageCustomerStage), [hasPolicy])

  /* ------------------------- システム管理系ポリシー -------------------------- */
  const canManageUser = useMemo(() => hasPolicy(Policy.manageUser), [hasPolicy])
  const canManageSecurity = useMemo(() => hasPolicy(Policy.manageSecurity), [hasPolicy])

  /* ------------------- セレクト関連 ------------------- */
  const hasCompanyPolicy = useCallback(
    (names: string[]) => {
      const id = masterData?.companyPolicies.find(p => names.includes(p.name))?.id
      return id ? profile?.customRoles?.some(r => r.policyIds.includes(id)) ?? false : false
    },
    [masterData?.companyPolicies, profile?.customRoles]
  )

  const hasSelectRead = useMemo(
    () => hasCompanyPolicy(['selectRead', 'セレクト参照']),
    [hasCompanyPolicy]
  )
  const hasSelectManage = useMemo(
    () => hasCompanyPolicy(['selectManage', 'セレクト管理']),
    [hasCompanyPolicy]
  )
  const hasSelectPrivilege = useMemo(
    () => hasCompanyPolicy(['selectPrivilege', 'セレクト特権']),
    [hasCompanyPolicy]
  )
  const hasSelectSystemRole = hasSelectRead || hasSelectManage || hasSelectPrivilege

  /* ----------------------- 対象ユーザー依存のチェック ----------------------- */
  const isSignInUser = ({ userId }: { userId: number }): boolean =>
    !!profile && profile.id === userId

  const canUpdateRole = ({ targetUser }: CheckRoleInput): boolean => {
    if (!profile) return false
    if (!canManageUser) return false
    if (targetUser.role.name === 'superAdmin') {
      return profile.role.name === 'superAdmin'
    }
    return true
  }

  const canDeleteUser = ({ targetUser }: CheckRoleInput): boolean =>
    !!profile && canManageUser && profile.id !== targetUser.id

  return {
    /* 状態 */
    isLoading,
    isError,

    /* プロジェクト */
    canCreateProject,
    canUpdateProject,
    canDeleteProject,
    canAssignProject,
    canReadReport,

    /* 共通コンテンツ */
    canReadCommonContent,
    canWriteCommonContent,
    canDeleteCommonContent,

    /* 顧客 */
    canOptOutCustomer,
    canDeleteCustomer,
    canManageCustomerStage,

    /* システム管理 */
    canManageUser,
    canManageSecurity,

    /* セレクト */
    hasSelectRead,
    hasSelectManage,
    hasSelectPrivilege,
    hasSelectSystemRole,

    /* 動的チェック関数 */
    isSignInUser,
    canUpdateRole,
    canDeleteUser,
  }
}
